'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface SidebarNavItemProps {
  href: string;
  icon: React.ReactNode;
  label: string;
  badge?: string | number;
  badgeVariant?: 'default' | 'secondary' | 'error' | 'outline' | 'primary' | 'success' | 'warning' | 'info';
  collapsed?: boolean;
}

export function SidebarNavItem({
  href,
  icon,
  label,
  badge,
  badgeVariant = 'secondary',
  collapsed = false,
}: SidebarNavItemProps) {
  const pathname = usePathname();
  const isActive = pathname === href || pathname?.startsWith(href + '/');

  const content = (
    <Link
      href={href}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors relative',
        'group',
        isActive
          ? 'bg-[var(--highlight-primary)] text-primary-600'
          : 'text-[var(--text-body)] hover:bg-[var(--surface-hover)]',
        collapsed && 'justify-center px-2'
      )}
    >
      {/* Active indicator bar */}
      {isActive && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary-600 rounded-r-full" />
      )}

      {/* Icon */}
      <span
        className={cn(
          'flex-shrink-0 w-5 h-5',
          isActive ? 'text-primary-600' : 'text-[var(--text-secondary)] group-hover:text-[var(--text-body)]'
        )}
      >
        {icon}
      </span>

      {/* Label */}
      {!collapsed && (
        <span className={cn('flex-1 text-sm font-medium', isActive && 'text-primary-600')}>
          {label}
        </span>
      )}

      {/* Badge */}
      {badge !== undefined && !collapsed && (
        <Badge variant={badgeVariant} className="h-5 px-2 text-xs">
          {badge}
        </Badge>
      )}
    </Link>
  );

  // Wrap in tooltip when collapsed
  if (collapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" className="flex items-center gap-2">
            <span>{label}</span>
            {badge !== undefined && (
              <Badge variant={badgeVariant} className="h-5 px-2 text-xs">
                {badge}
              </Badge>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
}
